import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Activity,
  AlertCircle,
  ArrowRight,
  Calculator,
  CheckCircle2,
  Database,
  FileCheck,
  FileSpreadsheet,
  RefreshCw,
  Shield,
  Upload
} from 'lucide-react';
import { API_BASE } from '@/shared/services/api';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Input } from '@/shared/components/ui/input';
import { Textarea } from '@/shared/components/ui/textarea';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('ezerpToken')}`
});

const FeatureCard = ({ title, value, subtitle, icon: Icon, color = 'green' }) => {
  const colors = {
    green: 'bg-green-500/20 text-green-500',
    blue: 'bg-blue-500/20 text-blue-500',
    amber: 'bg-amber-500/20 text-amber-500',
    red: 'bg-red-500/20 text-red-500',
    purple: 'bg-purple-500/20 text-purple-500'
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-lg ${colors[color] || colors.green}`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const PageHeader = ({ title, description, action }) => (
  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
    <div>
      <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{title}</h1>
      <p className="text-muted-foreground mt-1">{description}</p>
    </div>
    {action}
  </div>
);

const JsonResult = ({ result }) => (
  <Card>
    <CardHeader>
      <CardTitle className="text-lg">Last Result</CardTitle>
    </CardHeader>
    <CardContent>
      <pre className="bg-slate-950 text-slate-100 rounded-lg p-4 overflow-auto text-xs min-h-28">
        {result ? JSON.stringify(result, null, 2) : 'No execution yet.'}
      </pre>
    </CardContent>
  </Card>
);

const FiscalWorkflow = () => (
  <Card>
    <CardHeader>
      <CardTitle className="text-lg flex items-center gap-2">
        <Activity className="h-5 w-5 text-green-500" />
        Brazilian Fiscal Lifecycle
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="grid gap-3 md:grid-cols-5">
        {['Draft', 'Transmitted', 'Authorized', 'Apuração', 'SPED/EFD'].map((step, index) => (
          <div key={step} className="rounded-lg border p-4 text-center">
            <Badge className="mb-3 bg-green-500/20 text-green-500">{index + 1}</Badge>
            <p className="font-semibold">{step}</p>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

export const FiscalDashboard = () => {
  const quickActions = [
    { name: 'Fiscal Documents', href: '/fiscal/documents', icon: FileCheck },
    { name: 'Apuração', href: '/fiscal/apuracao', icon: Calculator },
    { name: 'SPED / EFD', href: '/fiscal/sped', icon: FileSpreadsheet },
    { name: 'Audit Evidence', href: '/fiscal/audit', icon: Shield }
  ];

  return (
    <div className="space-y-6" data-testid="fiscal-dashboard-page">
      <PageHeader
        title="Fiscal Engine"
        description="Brazil fiscal operations for SEFAZ documents, apuração, SPED/EFD exports, matrix rules, and audit evidence."
        action={<Link to="/fiscal/documents"><Button className="gap-2 bg-green-600 hover:bg-green-700"><FileCheck className="h-4 w-4" /> Open Documents</Button></Link>}
      />

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <FeatureCard title="SEFAZ Lifecycle" value="Ready" subtitle="Transmit and poll endpoints wired" icon={FileCheck} color="green" />
        <FeatureCard title="Apuração Engine" value="Ready" subtitle="Period calculation endpoint wired" icon={Calculator} color="blue" />
        <FeatureCard title="SPED/EFD" value="Ready" subtitle="EFD Contribuições endpoint wired" icon={FileSpreadsheet} color="amber" />
        <FeatureCard title="Audit Controls" value="Mapped" subtitle="Evidence and duplicate guard surfaced" icon={Shield} color="purple" />
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        {quickActions.map((action) => (
          <Link key={action.name} to={action.href}>
            <Card className="hover:border-green-500 transition-colors cursor-pointer h-full">
              <CardContent className="pt-6 flex flex-col items-center text-center">
                <action.icon className="h-8 w-8 text-green-500 mb-3" />
                <h3 className="font-semibold">{action.name}</h3>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <FiscalWorkflow />
    </div>
  );
};

export const FiscalDocumentsPage = () => {
  const [documentId, setDocumentId] = useState('');
  const [recPayload, setRecPayload] = useState('{"recibo": ""}');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const callDocumentEndpoint = async (action) => {
    if (!documentId.trim()) {
      toast.error('Document ID is required');
      return;
    }
    setLoading(true);
    try {
      const body = action === 'poll' ? JSON.parse(recPayload || '{}') : {};
      const res = await fetch(`${API_BASE}/api/fiscal/documents/${encodeURIComponent(documentId)}/${action}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body)
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || `Fiscal document ${action} failed`);
      setResult(json);
      toast.success(`Fiscal document ${action} completed`);
    } catch (err) {
      setResult({ error: err.message });
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="fiscal-documents-page">
      <PageHeader title="Fiscal Documents" description="Transmit fiscal documents and poll SEFAZ authorization status." />
      <FiscalWorkflow />
      <Card>
        <CardHeader><CardTitle>SEFAZ Actions</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <Input placeholder="Document ID" value={documentId} onChange={(event) => setDocumentId(event.target.value)} />
          <Textarea rows={4} value={recPayload} onChange={(event) => setRecPayload(event.target.value)} />
          <div className="flex flex-wrap gap-2">
            <Button disabled={loading} onClick={() => callDocumentEndpoint('transmit')} className="gap-2 bg-green-600 hover:bg-green-700">
              <Upload className="h-4 w-4" /> Transmit
            </Button>
            <Button disabled={loading} variant="outline" onClick={() => callDocumentEndpoint('poll')} className="gap-2">
              <RefreshCw className="h-4 w-4" /> Poll Authorization
            </Button>
          </div>
        </CardContent>
      </Card>
      <JsonResult result={result} />
    </div>
  );
};

export const FiscalApuracaoPage = () => {
  const [businessId, setBusinessId] = useState('');
  const [referencePeriod, setReferencePeriod] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const execute = async () => {
    if (!businessId || !referencePeriod) {
      toast.error('Business ID and reference period are required');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/fiscal/apuracao/${encodeURIComponent(businessId)}/${encodeURIComponent(referencePeriod)}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Apuração failed');
      setResult(json);
      toast.success('Apuração completed');
    } catch (err) {
      setResult({ error: err.message });
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="fiscal-apuracao-page">
      <PageHeader title="Apuração" description="Calculate Brazilian fiscal period liabilities from authorized fiscal documents." />
      <div className="grid gap-4 md:grid-cols-3">
        <FeatureCard title="ICMS" value="Mapped" subtitle="Tax base and debit/credit model" icon={Calculator} color="green" />
        <FeatureCard title="PIS/COFINS" value="Mapped" subtitle="Contribution calculation support" icon={Calculator} color="blue" />
        <FeatureCard title="Period Lock" value="Guarded" subtitle="Designed for auditable close" icon={Shield} color="amber" />
      </div>
      <Card>
        <CardHeader><CardTitle>Run Apuração</CardTitle></CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-[1fr_1fr_auto] md:items-end">
          <Input placeholder="Business ID" value={businessId} onChange={(event) => setBusinessId(event.target.value)} />
          <Input placeholder="Reference period, e.g. 2026-05" value={referencePeriod} onChange={(event) => setReferencePeriod(event.target.value)} />
          <Button disabled={loading} onClick={execute} className="gap-2 bg-green-600 hover:bg-green-700">
            <Calculator className="h-4 w-4" /> Execute
          </Button>
        </CardContent>
      </Card>
      <JsonResult result={result} />
    </div>
  );
};

export const FiscalSpedPage = () => {
  const [businessId, setBusinessId] = useState('');
  const [referencePeriod, setReferencePeriod] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const execute = async () => {
    if (!businessId || !referencePeriod) {
      toast.error('Business ID and reference period are required');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/fiscal/sped/efd-contribuicoes/${encodeURIComponent(businessId)}/${encodeURIComponent(referencePeriod)}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'SPED export failed');
      setResult(json);
      toast.success('EFD Contribuições generated');
    } catch (err) {
      setResult({ error: err.message });
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="fiscal-sped-page">
      <PageHeader title="SPED / EFD" description="Generate statutory Brazilian fiscal export files from the fiscal backbone." />
      <div className="grid gap-4 md:grid-cols-2">
        <FeatureCard title="EFD Contribuições" value="Endpoint Ready" subtitle="Generate through fiscal API" icon={FileSpreadsheet} color="green" />
        <FeatureCard title="EFD ICMS/IPI" value="Engine Present" subtitle="Backend service exists; API exposure pending" icon={AlertCircle} color="amber" />
      </div>
      <Card>
        <CardHeader><CardTitle>Generate EFD Contribuições</CardTitle></CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-[1fr_1fr_auto] md:items-end">
          <Input placeholder="Business ID" value={businessId} onChange={(event) => setBusinessId(event.target.value)} />
          <Input placeholder="Reference period, e.g. 2026-05" value={referencePeriod} onChange={(event) => setReferencePeriod(event.target.value)} />
          <Button disabled={loading} onClick={execute} className="gap-2 bg-green-600 hover:bg-green-700">
            <FileSpreadsheet className="h-4 w-4" /> Generate
          </Button>
        </CardContent>
      </Card>
      <JsonResult result={result} />
    </div>
  );
};

export const FiscalMatrixPage = () => {
  const dimensions = ['UF origem/destino', 'NCM', 'CFOP', 'CST/CSOSN', 'Regime tributário', 'Service code', 'Tax type'];

  return (
    <div className="space-y-6" data-testid="fiscal-matrix-page">
      <PageHeader title="Fiscal Matrix" description="Rule matrix for Brazilian fiscal determination and accounting handoff." />
      <Card>
        <CardHeader><CardTitle>Rule Dimensions</CardTitle></CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-3">
            {dimensions.map((item) => (
              <div key={item} className="rounded-lg border p-4 flex items-center gap-3">
                <Database className="h-5 w-5 text-green-500" />
                <span className="font-medium">{item}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-6 text-muted-foreground">
          Fiscal matrix models are present in the backend. Registry/list management API is not exposed yet, so this page surfaces the official scope without inventing data.
        </CardContent>
      </Card>
    </div>
  );
};

export const FiscalEventsPage = () => (
  <div className="space-y-6" data-testid="fiscal-events-page">
    <PageHeader title="Fiscal Events" description="Fiscal webhook and event lifecycle visibility for EzERP fiscal operations." />
    <div className="grid gap-4 md:grid-cols-3">
      <FeatureCard title="Webhook" value="/api/fiscal/webhook/ezerp" subtitle="Inbound fiscal events" icon={Activity} color="green" />
      <FeatureCard title="SEFAZ Status" value="Tracked" subtitle="Authorization lifecycle states" icon={CheckCircle2} color="blue" />
      <FeatureCard title="Failures" value="Visible" subtitle="Error payloads and retry context" icon={AlertCircle} color="red" />
    </div>
    <Card>
      <CardHeader><CardTitle>Event Backbone</CardTitle></CardHeader>
      <CardContent className="text-muted-foreground">
        Events are handled by the fiscal backend and linked to document transmission, polling, apuração, and statutory export readiness.
      </CardContent>
    </Card>
  </div>
);

export const FiscalAuditEvidencePage = () => (
  <div className="space-y-6" data-testid="fiscal-audit-page">
    <PageHeader title="Audit Evidence" description="Evidence map for fiscal exports, SEFAZ protocols, apuração records, and close controls." />
    <Card>
      <CardHeader><CardTitle>Evidence Coverage</CardTitle></CardHeader>
      <CardContent>
        <div className="space-y-3">
          {[
            'SEFAZ protocol and authorization status are retained per fiscal document.',
            'Apuração produces period-level fiscal calculation records.',
            'SPED/EFD generation records output metadata for audit review.',
            'Duplicate-export and period-lock rules are surfaced as fiscal control requirements.'
          ].map((item) => (
            <div key={item} className="flex items-start gap-3 rounded-lg border p-4">
              <Shield className="h-5 w-5 text-green-500 mt-0.5" />
              <p>{item}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
    <Link to="/fiscal/sped">
      <Button variant="outline" className="gap-2">Review SPED / EFD <ArrowRight className="h-4 w-4" /></Button>
    </Link>
  </div>
);

